import SEO from '@/seo';
import { Link } from '@holo/link';
import { Container, H1 } from '@holo/ui';
import type { GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  return {
    props: await serverSideTranslations(locale ?? 'en'),
  };
};

const Page = () => {
  return (
    <Container maxWidth={700} marginTop={80} className="content">
      <SEO title="Privacy policy" />
      <H1 marginBottom={20}>Privacy policy</H1>
      <p>
        <strong>1. Introduction</strong>
        <br />
        This Privacy Policy is intended to help you better understand how we collect, use and store your personal
        information—whether you are a merchant or end user that uses Holo’s products, applications or services
        (together, the “Services”), a customer that shops at a store using our technology or whether you’re simply
        visiting this website. By using any of Holo’s Services, or by dealing with a merchant using Holo’s Services, you
        are agreeing to the terms of this Privacy Policy and, as applicable, the Holo{' '}
        <Link href="/terms-of-service">Terms of Service</Link>.
        <br />
        We may update this Privacy Policy from time to time in order to reflect, for example, changes to our privacy
        practices or for other operational, legal, or regulatory reasons. If we make material changes to this Privacy
        Policy, we will give you notice of such changes by posting the revised policy on this Website, and where
        appropriate, by other means. By continuing to use this Website or the Support Service after these changes are
        posted, you agree to the revised policy.
      </p>
      <p>
        <strong>2. Information from merchants</strong>
        <br />
        <ul>
          <li>We collect your name, company name, address, email address, phone number(s) and credit card details.</li>
          <li>
            We need this information to provide you with our Services; for example, to confirm your identity, contact
            you, and invoice you.
          </li>
          <li>We will also use Personal Information in other cases where you have given us your express permission.</li>
          <li>
            When using the Delivery functionality included in the Holo POS app we will also collect location
            information. Location data is collected when you start a route and until the last order of the route is
            delivered, even when the app is closed or not in use. Location data will only be used to allow customers to
            view location of their order while it is out for delivery. Location data will not be used for any other
            purposes.
          </li>
        </ul>
      </p>
      <p>
        <strong>3. Information from our merchants’ customers</strong>
        <br />
        <ul>
          <li>
            We collect our merchants’ customers’ name, email, shipping and billing address, payment details, company
            name, phone number, IP address and device data.
          </li>
          <li>
            We need this information to provide merchants with our Services, including supporting and processing orders,
            authentication, and processing payments. We also use this information to improve our Services.
          </li>
        </ul>
      </p>
      <p>
        <strong>4. Information from cookies and similar tracking technology</strong>
        <br />
        What is a cookie? A cookie is a small amount of data, which may include a unique identifier. Cookies are sent to
        your browser from a website and stored on your device. We assign a different cookie to each device that accesses
        our website.
        <ul>
          <li>We use cookies to recognize your device and provide you with a personalized experience.</li>
        </ul>
      </p>
      <p>
        <strong>5. Information from cookies and similar tracking technology</strong>
        <ul>
          <li>
            We will continue to store archived copies of your Personal Information for legitimate business purposes and
            to comply with the law.
          </li>
        </ul>
      </p>
      <p>
        <strong>6. What we don’t do with your Personal Information</strong>
        <ul>
          <li>
            We do not and will never share, disclose, sell, rent, or otherwise provide Personal Information to other
            companies.
          </li>
        </ul>
      </p>
    </Container>
  );
};

export default Page;
